'use memo';

import { Link } from '@tanstack/react-router';
import TrendBadge from '@/components/Badge/TrendBadge';
import { StatCardHighlight, StatCardPrimaryValue, StatCardSecondaryValue, StatCardValueContainer } from '@/components/Card/StatCard';
import Icon from '@/components/Icon/Icon';
import { HStack } from '@/components/Layout';
import Spacer from '@/components/Layout/Spacer';
import { Text } from '@/components/Typography';
import { Separator } from '@/components/ui/separator';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { DormantSummaryResponse } from '@/routes/_staff/accesses/_layout/alerts/-dormancy-query/type';
import { BentoCard, SeeMoreButton } from './BentoCard';

/** カード3: 休眠候補（件数・対象ソフト・閾値・前月比）＋推定削減額（Coming soon）。 */
export function DormancyCard({
  data
}: {
  data: DormantSummaryResponse | undefined;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.accesses.overview.bento'
  });
  const {
    t: tDormancy
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.accesses.dormancy.summary'
  });
  return <BentoCard title={t('dormancy.title')} headerRight={<SeeMoreButton>
          <Link to="/accesses/alerts" search={{
      alertTab: 'dormant-it'
    }}>
            {t('seeMore')}
          </Link>
        </SeeMoreButton>}>
      <StatCardHighlight className="py-2">
        <StatCardValueContainer>
          <StatCardPrimaryValue>
            {(data?.dormantAccountCount ?? 0).toLocaleString()}
          </StatCardPrimaryValue>
          <StatCardSecondaryValue>
            {t('dormancy.accountUnit')}
          </StatCardSecondaryValue>
        </StatCardValueContainer>
      </StatCardHighlight>
      <Spacer variant="xs" />
      <Separator />
      <Spacer variant="xs" />

      {/* サブ指標: ラベル行・値行を grid で左右・上下に揃える */}
      <div className="grid grid-cols-3 gap-x-6 gap-y-1">
        <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
          {t('dormancy.affectedSoftware')}
        </Text>
        <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
          {t('dormancy.threshold')}
        </Text>
        <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
          {t('dormancy.monthOverMonth')}
        </Text>

        <Text as="p" variant="numberSemiLarge" className="flex h-7 items-center">
          {(data?.affectedSoftwareCount ?? 0).toLocaleString()}
        </Text>
        <Text as="p" variant="numberSemiLarge" className="flex h-7 items-end">
          {data?.thresholdMonths ?? 1}
          <span className="ml-0.5 pb-0.5 text-pSmall font-semibold text-neutral-muted-on-fill">
            {tDormancy('monthUnit')}
          </span>
        </Text>
        <div className="flex h-7 items-center">
          <TrendBadge value={data?.dormantAccountCountChange ?? 0} variant="number" color="neutral" />
        </div>
      </div>

      <HStack className="mt-2 items-center justify-between gap-2 rounded-md bg-neutral-muted-fill/50 px-3 py-2">
        <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
          {t('dormancy.estimatedSaving')}
        </Text>
        <HStack className="items-center gap-1.5 text-neutral-muted-on-fill">
          <Icon iconName="RocketIcon" size={14} />
          <Text variant="labelSmall">{t('comingSoon')}</Text>
        </HStack>
      </HStack>
    </BentoCard>;
}